// declare function assert(value: unknown): asserts value; https://duckduckgo.com/?q=typescript+assertion+function+with+message&t=osx&ia=web
import { SyntaxNode } from "web-tree-sitter";
import { getTextRange } from "../text_range";
import assert from "assert";
import { TreeSitterLanguage } from "./tree-sitter-language";
import { BinaryGeneralOperator, BinaryNumericalOperator, Block, Identifier, If } from "../interpretable";
import { InterpType } from "../interpretable";
import { v4 } from "uuid";
import { DataType } from "../interpreter";

const indentBy = "  ";
function addIndent(string: string) {
  if (string == "") {
    return "";
  } else {
    return `${indentBy}${string.replace("\n", `\n${indentBy}`)}`;
  }
}

export class Python extends TreeSitterLanguage {
  name = "python";
  public async initialize(): Promise<void> {
    await this.initializeParserForLanguage("python");
  }

  protected fromTreeSitter(node: SyntaxNode | null, parent: string | null): string {
    // sometimes you want to convert a node that does not exist.
    // isMissing can be true due to a syntax error, but it will always be wrapped in a node of type 'error'
    if (!node || node.isMissing()) {
      return undefined;
    }

    if (node.hasError()) {
      console.log(
        node.text,
        node.isMissing(),
        node.children.map((ch) => ch.text)
      );
      return;
    }

    // we can pass through expression statements in the tree_sitter structure.
    // can't be in the switch as we create a new uuid and it would be harder to keep track.
    if (node.type == "expression_statement") {
      assert(node.childCount == 1);
      return this.fromTreeSitter(node.children[0], parent);
    }

    const uuid = v4();
    // massive switch statement for each TreeSitter node type!
    // see https://tree-sitter.github.io/tree-sitter/playground
    switch (node.type) {
      case "block":
      case "module": {
        this.addInterpretable(uuid, {
          type: InterpType.Block,
          children: node.children.map((child) => this.fromTreeSitter(child, uuid)).filter((node) => node !== undefined),
          parent: parent,
        }, getTextRange(node));
        return uuid;
      }
      /*
      case "elif_clause": {
        console.log(parent)
        // in order to highlight the line properly during execution, we want to highlight
        // the "if", the condition, and the ":", but not the entire block (including consequence, alternative).
        const if_text = node.descendantsOfType("elif")[0];
        const colon_text = node.descendantsOfType(":")[0];
        assert(if_text && if_text.type == "elif");
        assert(colon_text && colon_text.type == ":");
        const textRange = getTextRange(if_text, colon_text);

        const elifNodeUUID = v4();

        const condition = this.convertChild(node, "condition", elifNodeUUID);
        const consequence = this.convertChild(node, "consequence", elifNodeUUID);
        const alternative = this.convertOptionalChild(node, "alternative", elifNodeUUID);

        console.log(this.interpretables.get(consequence))
        const elifNode: If = {
          type: InterpType.If,
          children: {
            condition: condition,
            then: consequence,
            else: alternative,
          },
          parent: parent,
        };

        const blockNode: Block = {
          type: InterpType.Block,
          children: [elifNodeUUID],
          parent: parent,
        };
        this.addInterpretable(uuid, blockNode, textRange);
        this.addInterpretable(elifNodeUUID, elifNode, textRange)
        return uuid;
      }*/
      case "if_statement": {
        // in order to highlight the line properly during execution, we want to highlight
        // the "if", the condition, and the ":", but not the entire block (including consequence, alternative).
        const if_text = node.descendantsOfType("if")[0];
        const colon_text = node.descendantsOfType(":")[0];
        assert(if_text && if_text.type == "if");
        assert(colon_text && colon_text.type == ":");
        const textRange = getTextRange(if_text, colon_text);

        const condition = this.convertChild(node, "condition", uuid);
        const consequence = this.convertChild(node, "consequence", uuid);
        const alternative = this.convertOptionalChild(node, "alternative", uuid);

        this.addInterpretable(uuid, {
          type: InterpType.If,
          children: {
            condition: condition,
            then: consequence,
            else: alternative,
          },
          parent: parent,
        }, textRange);
        return uuid;
      }
      case "while_statement": {
        const while_text = node.descendantsOfType("while")[0];
        const colon_text = node.descendantsOfType(":")[0];
        // sanity check:
        assert(while_text && while_text.type == "while");
        assert(colon_text && colon_text.type == ":");
        const textRange = getTextRange(while_text, colon_text); // see if node's text range
        const condition = this.convertChild(node, "condition", uuid);
        const body = this.convertChild(node, "body", uuid);

        this.addInterpretable(uuid, {
          type: InterpType.While,
          children: {
            condition: condition,
            body: body,
          },
          parent: parent,
        }, textRange);
        return uuid;
      }
      case "for_statement": {
        // Handles `for i in range` as well as `for i in array`.
        // these are handled by two seperate disjoint Interpretables.
        const for_text = node.descendantsOfType("for")[0];
        const colon_text = node.descendantsOfType(":")[0];
        const textRange = getTextRange(for_text, colon_text);

        const identifier = this.convertChild(node, "left", uuid);
        const right = this.convertChild(node, "right", uuid);
        const body = this.convertChild(node, "body", uuid);

        // checking for existence of the range() function so we can choose between
        // ForLoop and ForIter.
        const rightInterp = this.interpretables.get(right);

        if (rightInterp.type == InterpType.Call && this.interpretables.get(rightInterp?.children.identifier) && (this.interpretables.get(rightInterp?.children.identifier) as Identifier).data.name == "range") {
          // remove the range() as we treat `for x in range()` as a seperate for node entirely.
          this.interpretables.delete(right);
          var rangeArgs = {
            start: undefined,
            stop: undefined,
            step: undefined,
          };
          console.log(rightInterp.children.arguments)
          // get proper arguments.
          // range(stop), range(start, stop) and range(start, stop, step) are all valid and we need
          // to respect that positional change from 1 arg to 2 args in terms of the length of the arguments
          switch (rightInterp.children.arguments.length) {
            case 1: {
              rangeArgs = {
                start: undefined,
                stop: rightInterp.children.arguments[0],
                step: undefined,
              };
              break;
            }
            case 2: {
              rangeArgs = {
                start: rightInterp.children.arguments[0],
                stop: rightInterp.children.arguments[1],
                step: undefined,
              };
              break;
            }
            case 3: {
              rangeArgs = {
                start: rightInterp.children.arguments[0],
                stop: rightInterp.children.arguments[1],
                step: rightInterp.children.arguments[2],
              };
              break;
            }

            default:
              assert(rightInterp.children.arguments.length > 0, `range function called with no args.`);
              assert(rightInterp.children.arguments.length <= 3, `range function called with over 3 args.`);
              break;
          }
          this.addInterpretable(uuid, {
            type: InterpType.ForLoop,
            children: {
              identifier: identifier,
              ...rangeArgs,
              body: body,
            },
            parent: parent,
          }, textRange);
          return uuid;
        } else {
          this.addInterpretable(uuid, {
            type: InterpType.ForIter,
            children: {
              identifier: identifier,
              body: body,
              iterable: right,
            },
            parent: parent,
          }, textRange);
          return uuid;
        }
        break;
      }
      case "boolean_operator":
      case "binary_operator":
      case "comparison_operator": {
        const left = this.fromTreeSitter(node.children[0], uuid);
        const comparisonOperator = node.children[1].text;
        assert(
          ["==", "!=", "or", "and", "<", "<=", ">=", ">", "+", "-", "*", "**", "%", "/"].includes(comparisonOperator),
          `operator ${comparisonOperator} is not supported.`
        );
        const right = this.fromTreeSitter(node.children[2], uuid);

        var interpretableType: InterpType.BinaryGeneralOp | InterpType.BinaryNumericalOp;
        if (["==", "!=", "or", "and"].includes(comparisonOperator)) {
          interpretableType = InterpType.BinaryGeneralOp;
        } else {
          interpretableType = InterpType.BinaryNumericalOp;
        }

        this.addInterpretable(uuid, {
          type: interpretableType,
          data: {
            operator: comparisonOperator,
          },
          children: {
            left: left,
            right: right,
          },
          parent: parent,
        } as BinaryGeneralOperator | BinaryNumericalOperator,
        getTextRange(node));
        return uuid;
      }
      case "function_definition": {
        const identifier = this.convertChild(node, "name", uuid);
        const {parameters, parameterTypes} = this.createParams(node.childForFieldName("parameters"), uuid);
        const body = this.convertChild(node, "body", uuid);
        this.addInterpretable(uuid, {
          type: InterpType.FunctionDef,
          data: {
            parameterTypes: parameterTypes
          },
          children: {
            identifier: identifier,
            body: body,
            parameters: parameters
          },
          parent: parent,
        }, getTextRange(node));
        return uuid;
      }
      case "call": {
        const identifier = this.convertChild(node, 'function', uuid);
        const args = node
          .childForFieldName("arguments")
          .children
          .filter((child) => child.type != "(" && child.type != ")" && child.type != ",")
          .map(child => this.fromTreeSitter(child, uuid))
        this.addInterpretable(uuid, {
          type: InterpType.Call,
          children: {
            identifier: identifier,
            arguments: args
          },
          parent: parent
        }, getTextRange(node))
        return uuid;
      }
      case "break_statement": {
        this.addInterpretable(uuid, {
          type: InterpType.Break,
          parent: parent
        }, getTextRange(node))
        return uuid;
      }
      case "return_statement": {
        // we handle both 'return' or 'return withData'
        const withData = node.children.length == 2 ? this.fromTreeSitter(node.children.at(-1), uuid) : undefined;

        this.addInterpretable(uuid, {
          type: InterpType.Return,
          children: {
            withData: withData
          },
          parent: parent
        }, getTextRange(node))
        return uuid;
      }
      case "identifier": {
        this.addInterpretable(uuid, {
          type: InterpType.Identifier,
          data: {
            name: node.text,
          },
          parent: parent,
        }, getTextRange(node));
        return uuid;
      }
      case "subscript": {
        this.addInterpretable(uuid, {
          type: InterpType.Subscript,
          children: {
            array: this.convertChild(node, "value", uuid),
            subscript: this.convertChild(node, "subscript", uuid)
          },
          parent: parent,
        }, getTextRange(node));
        return uuid;
      }
      case "float": {
        this.addInterpretable(uuid, {
          type: InterpType.DataLiteral,
          data: {
            data: parseFloat(node.text),
            type: "float",
          },
          parent: parent,
        }, getTextRange(node));
        return uuid;
      }
      case "integer": {
        this.addInterpretable(uuid, {
          type: InterpType.DataLiteral,
          data: {
            data: parseInt(node.text),
            type: "integer",
          },
          parent: parent,
        }, getTextRange(node));
        return uuid;
      }
      case "string": {
        this.addInterpretable(uuid, {
          type: InterpType.DataLiteral,
          data: {
            data: node.children[1].text, // children[1] is the middle of the string
            type: "string",
          },
          parent: parent,
        }, getTextRange(node));
        return uuid;
      }
      // even though python has uppercase booleans, tree sitter interprets them as lowercase:
      case "true": {
        this.addInterpretable(uuid, {
          type: InterpType.DataLiteral,
          data: {
            data: true,
            type: "boolean",
          },
          parent: parent,
        }, getTextRange(node));
        return uuid;
      }
      case "false": {
        this.addInterpretable(uuid, {
          type: InterpType.DataLiteral,
          data: {
            data: false,
            type: "boolean",
          },
          parent: parent,
        }, getTextRange(node));
        return uuid;
      }
      case "pass_statement": {
        return undefined;
      }
      case "list": {
        const children = node.children
          .filter((node) => node.type != "[" && node.type != "]" && node.type != ",")
          .map((child) => this.fromTreeSitter(child, uuid));
        this.addInterpretable(uuid, {
          type: InterpType.ArrayLiteral,
          children: children,
          parent: parent,
        }, getTextRange(node));
        return uuid;
      }
      // used in the if node:
      case "else_clause": {
        return this.convertChild(node, "body", uuid);
      }
      case "assignment": {
        this.addInterpretable(uuid, {
          type: InterpType.Assignment,
          data: {
          },
          children: {
            left: this.convertChild(node, "left", uuid),
            right: this.convertChild(node, "right", uuid),
          },
          parent: parent,
        }, getTextRange(node));
        return uuid;
      }
      default: {
        console.error("unknown node type " + node.type);
      }
    }
  }

  // this contains a massive if-else chain with templates.
  toRepresentation(interpretable: Interpretable): string {
    if (interpretable instanceof AssignmentNode) {
      return `${interpretable.identifier.identifier} = ${this.toRepresentation(interpretable.right)}`;
    } else if (interpretable instanceof BlockNode) {
      return interpretable.children.map((child) => this.toRepresentation(child)).join("\n");
    } else if (interpretable instanceof BreakNode) {
      return "break";
    } else if (interpretable instanceof CallNode) {
      const args = interpretable.fnArgs.map((arg) => this.toRepresentation(arg)).join(", ");
      return `${interpretable.identifier}(${args})`;
    } else if (interpretable instanceof DataLiteralNode) {
      if (interpretable.dataType == "string") {
        return `'${interpretable.data}'`;
      } else if (interpretable.data === undefined) {
        return "null";
      } else if (interpretable.data === true) {
        return "True";
      } else if (interpretable.data === false) {
        return "False";
      } else {
        return interpretable.data?.toString();
      }
    } else if (interpretable instanceof FunctionNode) {
      const params = interpretable.fnParams.map((param) => `${param.name}`).join(", ");
      const def = `def ${this.toRepresentation(interpretable.identifier)}(${params}):`;
      const body = addIndent(this.toRepresentation(interpretable.body));
      console.log(body);
      return `${def}\n${body}`;
    } else if (interpretable instanceof IdentifierNode) {
      return interpretable.identifier;
    } else if (interpretable instanceof IfNode) {
      const if_statement = `if ${this.toRepresentation(interpretable.condition)}:`;
      const consequence = addIndent(this.toRepresentation(interpretable.consequence));
      if (interpretable.alternative) {
        const alternative = addIndent(this.toRepresentation(interpretable.alternative));
        return `${if_statement}\n${consequence}else:\n${alternative}`;
      }
      return `${if_statement}\n${consequence}`;
    } else if (interpretable instanceof PassNode) {
      return "pass";
    } else if (interpretable instanceof ReturnNode) {
      if (!interpretable.node) {
        return "return";
      } else {
        return `return ${this.toRepresentation(interpretable.node)}`;
      }
    } else if (interpretable instanceof ComparisonNode) {
      return `${this.toRepresentation(interpretable.left)} ${interpretable.operator} ${this.toRepresentation(
        interpretable.right
      )}`;
    } else if (interpretable instanceof UnaryOperatorNode) {
      return `${interpretable.operator} ${this.toRepresentation(interpretable.node)}`;
    } else if (interpretable instanceof WhileNode) {
      const while_statement = `while ${this.toRepresentation(interpretable.condition)}:`;
      const consequence = addIndent(this.toRepresentation(interpretable.consequence));
      return `${while_statement}\n${consequence}`;
    } else if (interpretable instanceof ArrayLiteralNode) {
      return `[${interpretable.children.map((child) => this.toRepresentation(child)).join(", ")}]`;
    } else if (interpretable instanceof IterateNode) {
      return `for ${this.toRepresentation(interpretable.identifier)} in ${this.toRepresentation(
        interpretable.right
      )}:\n${addIndent(this.toRepresentation(interpretable.body))}`;
    } else if (interpretable instanceof ForNode) {
      var rangeParams = [interpretable.start, interpretable.step, interpretable.stop]
        .filter((item) => item !== undefined)
        .map((item) => this.toRepresentation(item))
        .join(", ");

      return `for ${this.toRepresentation(interpretable.identifier)} in range(${rangeParams}):\n${addIndent(
        this.toRepresentation(interpretable.body)
      )}`;
    }

    console.error("from representation: couldn't categorize this interpretable:", interpretable);
    return "ERROR_SEE_CONSOLE";
  }

  /**
   * Helper method.
   * Creates a list of FunctionParameters used in the FunctionNode.
   * This is representative of the `a, b, c` in `def func(a, b, c)`
   *
   * See https://tree-sitter.github.io/tree-sitter/playground
   * @param node a node of type 'parameters', see the tree sitter playground
   * @returns parameters (uuid list) and their types (DataType list)
   */
  private createParams(node: SyntaxNode, parentUUID: string): {parameters: string[], parameterTypes: DataType[]} {
    assert(node.type == "parameters", `node type must be parameters for createParams`);
    const parameters: string[] = [];
    const parameterTypes: DataType[] = [];
    for (const child of node.descendantsOfType("identifier")) {
      // why these are in here? i have no idea why but they are.
      if (child.type == "(" || child.type == ")") {
        continue;
      }
      assert(child.type != "typed_parameter", "typed parameters not supported yet.");
      assert(child.type == "identifier", `expected identifier for parameter but got ${child.type}`);
      parameters.push(this.fromTreeSitter(child, parentUUID))
      parameterTypes.push("unknown")
    }
    return {parameters: parameters, parameterTypes: parameterTypes};
  }
}
